// src/store/useCurrencyStore.js

import { create } from 'zustand';

const LS_KEY = '__astorius_currency__';

// Правильные курсы к AED (сколько единиц валюты за 1 AED)
const DEFAULT_RATES = {
  AED: 1,           // 1 AED = 1 AED
  USD: 0.2723,      // 1 AED = 0.2723 USD
  EUR: 0.2488,      // 1 AED = 0.2488 EUR  
  RUB: 27.027,      // 1 AED = 27.027 RUB
};

export const useCurrencyStore = create((set, get) => ({
  currency: (() => {
    try { return localStorage.getItem(LS_KEY) || 'RUB'; } catch { return 'RUB'; }
  })(),
  rates: DEFAULT_RATES,

  setCurrency: (cur) => {
    try { localStorage.setItem(LS_KEY, cur); } catch {}
    set({ currency: cur });
  },

  // Обновление курсов из API - но с защитой от неправильных серверных курсов
  setRates: (nextRates) => {
    // Проверяем, что серверные курсы разумные, иначе используем DEFAULT_RATES
    const validatedRates = { ...DEFAULT_RATES };
    
    if (nextRates) {
      Object.keys(nextRates).forEach(currency => {
        const rate = parseFloat(nextRates[currency]);
        // Проверяем, что курс в разумных пределах
        if (!isNaN(rate) && rate > 0) {
          // Для USD: должен быть около 0.27, не 1.0
          if (currency === 'USD' && (rate < 0.2 || rate > 0.35)) {
            console.warn(`Suspicious USD rate from server: ${rate}, using default: ${DEFAULT_RATES.USD}`);
            return;
          }
          // Для EUR: должен быть около 0.25, не 1.0  
          if (currency === 'EUR' && (rate < 0.2 || rate > 0.35)) {
            console.warn(`Suspicious EUR rate from server: ${rate}, using default: ${DEFAULT_RATES.EUR}`);
            return;
          }
          // Для RUB: должен быть около 27, не 1.0
          if (currency === 'RUB' && (rate < 20 || rate > 35)) {
            console.warn(`Suspicious RUB rate from server: ${rate}, using default: ${DEFAULT_RATES.RUB}`);
            return;
          }
          validatedRates[currency] = rate;
        } else {
          console.warn(`Invalid rate for ${currency}: ${rate}, using default: ${DEFAULT_RATES[currency]}`);
        }
      });
    }
    
    set({ rates: validatedRates });
  },

  // Convert price from one currency to another
  convertPrice: (amount, fromCurrency, toCurrency) => {
    const { rates } = get();
    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[toCurrency] || 1;
    
    // Сначала конвертируем в AED, потом в целевую валюту
    const amountInAED = Number(amount) / fromRate;
    const convertedAmount = amountInAED * toRate;
    
    return Math.round(convertedAmount);
  },

  // Новая функция: форматирование цены с учетом исходной валюты
  formatPriceFromCurrency: (price, fromCurrency, toCurrency = null) => {
    const { currency, rates } = get();
    const targetCurrency = toCurrency || currency;
    
    if (fromCurrency === targetCurrency) {
      // Если валюты одинаковые, просто форматируем
      const sym = {
        AED: 'د.إ',
        USD: '$',
        EUR: '€',
        RUB: '₽',
      }[targetCurrency] || targetCurrency;
      
      const n = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(price));
      return `${n} <span class="currency-symbol">${sym}</span>`;
    }
    
    // Конвертируем через AED
    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[targetCurrency] || 1;
    
    // Сначала в AED, потом в целевую валюту
    const amountInAED = Number(price) / fromRate;
    const convertedAmount = amountInAED * toRate;
    
    const sym = {
      AED: 'д.إ',
      USD: '$',
      EUR: '€',
      RUB: '₽',
    }[targetCurrency] || targetCurrency;
    
    const n = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(convertedAmount));
    const result = `${n} <span class="currency-symbol">${sym}</span>`;
    
    return result;
  },

  // priceAED — цена в AED; вернём строку с символом валюты
  formatPrice: (priceAED) => {
    const { currency, rates } = get();
    const rate = rates[currency] || 1;
    
    // Конвертируем из AED в выбранную валюту (умножаем на курс)
    const amount = (Number(priceAED || 0) * rate);

    const sym = {
      AED: 'د.إ',
      USD: '$',
      EUR: '€',
      RUB: '₽',
    }[currency] || currency;

    // короткая запись с разделителями
    const n = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(amount));
    const result = `${n} <span class="currency-symbol">${sym}</span>`;
    
    return result;
  },
}));
